/**
 * @file Shared grouping engine for board and table layouts.
 * It resolves group definitions and item membership while preserving the
 * incoming item order from the caller.
 */

import { appData, state } from "../../app/state/state.js";
import {
  getBoardDateGroupDefinitions,
  getBoardStatusConfig,
  getBoardStatusField,
  getBoardSortedCategories,
  getBoardTypeConfig,
  getBoardUrgencyConfig,
} from "./boardGrouping.js";
import { getResolvedCardDate } from "./itemSortUtils.js";
import {
    getMonthBoundaries,
    getWeekBoundaries,
} from "../utils/dateUtils.js";
import {
    getPatientProviderDisplayName,
} from "../utils/providerUtils.js";
import {
    getUserDisplayNameById,
    getUsersWithPermission,
} from "../utils/userDisplay.js";
import {
    isItemClosed,
} from "../utils/closedState.js";

function normaliseDate(dateValue) {
  if (!dateValue) {return null;}

  try {
    const parsedDate = new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
  } catch {
    return null;
  }
}

function getUpdatedAtValue(item) {
  const updatedAt = item?.updatedAt;
  if (!updatedAt) {return 0;}

  if (typeof updatedAt?.toMillis === "function") {
    return updatedAt.toMillis();
  }
  if (typeof updatedAt?.seconds === "number") {
    const nanos = typeof updatedAt?.nanoseconds === "number" ?
      updatedAt.nanoseconds : 0;
    return (updatedAt.seconds * 1000) + Math.floor(nanos / 1000000);
  }

  try {
    const parsedDate = new Date(updatedAt);
    return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
  } catch {
    return 0;
  }
}

function buildDateGroups(mode, showClosed) {
  const groups = getBoardDateGroupDefinitions(mode)
    .map((group) => ({ ...group, items: [] }));

  if (showClosed) {
    groups.push({ id: "closed", title: "Closed", items: [] });
  }

  return groups;
}

function getDateGroupId(dateValue, mode) {
  const resolvedDate = normaliseDate(dateValue);
  if (!resolvedDate) {return "later";}

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (resolvedDate < today) {
    return "overdue";
  }

  if (mode === "daily") {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (resolvedDate.getTime() === today.getTime()) {return "today";}
    if (resolvedDate.getTime() === tomorrow.getTime()) {return "tomorrow";}
    return "later";
  }

  if (mode === "weekly") {
    const {
      startOfWeek: thisWeekStart,
      endOfWeek: thisWeekEnd,
    } = getWeekBoundaries(today);
    const nextWeekDate = new Date(today);
    nextWeekDate.setDate(today.getDate() + 7);
    const {
      startOfWeek: nextWeekStart,
      endOfWeek: nextWeekEnd,
    } = getWeekBoundaries(nextWeekDate);

    if (resolvedDate >= thisWeekStart && resolvedDate <= thisWeekEnd) {
      return "thisWeek";
    }
    if (resolvedDate >= nextWeekStart && resolvedDate <= nextWeekEnd) {
      return "nextWeek";
    }
    return "later";
  }

  const {
    startOfMonth: thisMonthStart,
    endOfMonth: thisMonthEnd,
  } = getMonthBoundaries(today);
  const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const {
    startOfMonth: nextMonthStart,
    endOfMonth: nextMonthEnd,
  } = getMonthBoundaries(nextMonthDate);

  if (resolvedDate >= thisMonthStart && resolvedDate <= thisMonthEnd) {
    return "thisMonth";
  }
  if (resolvedDate >= nextMonthStart && resolvedDate <= nextMonthEnd) {
    return "nextMonth";
  }

  return "later";
}

function buildStatusGroups(itemType, showClosed) {
  const rawCategories = getBoardSortedCategories(itemType);
  if (rawCategories.length === 0) {
    return appendClosedGroup(getBoardStatusConfig(itemType)
      .filter((status) => showClosed || status?.isClosed !== true)
      .slice()
      .sort((a, b) => {
        const orderDiff = (a?.order ?? 999) - (b?.order ?? 999);
        if (orderDiff !== 0) {return orderDiff;}
        return (a?.name || "").localeCompare(b?.name || "");
      })
      .map((status) => ({
        id: status.id,
        title: status.name || "Unknown Status",
        color: status.color || "#d1d5db",
        isClosed: status.isClosed === true,
        items: [],
      })), showClosed);
  }

  const categories = rawCategories
    .filter((category) => showClosed || category?.isClosed !== true)
    .map((category) => ({
      id: category.id,
      title: category.name || "Unknown Category",
      color: category.color || "#cccccc",
      isClosed: category.isClosed === true,
      items: [],
    }));

  categories.push({
    id: "uncategorised",
    title: "Uncategorised",
    color: "#d1d5db",
    items: [],
    isFallback: true,
  });

  return categories;
}

function buildTypeGroups(itemType) {
  const typeGroups = getBoardTypeConfig(itemType)
    .slice()
    .sort((a, b) => {
      const orderDiff = (a?.order ?? 999) - (b?.order ?? 999);
      if (orderDiff !== 0) {return orderDiff;}
      return (a?.name || "").localeCompare(b?.name || "");
    })
    .map((typeOption) => ({
      id: typeOption.id,
      title: typeOption.name || "Unknown Type",
      color: typeOption.color || "#d1d5db",
      items: [],
    }));

  typeGroups.push({
    id: "none",
    title: "None",
    color: "#e5e7eb",
    items: [],
    isFallback: true,
  });
  typeGroups.push({
    id: "uncategorised",
    title: "Uncategorised",
    color: "#d1d5db",
    items: [],
    isFallback: true,
  });

  return typeGroups;
}

function buildUrgencyGroups(itemType) {
  const urgencyGroups = getBoardUrgencyConfig(itemType)
    .slice()
    .sort((a, b) => {
      const orderDiff = (a?.order ?? 999) - (b?.order ?? 999);
      if (orderDiff !== 0) {return orderDiff;}
      return (a?.name || "").localeCompare(b?.name || "");
    })
    .map((urgencyOption) => ({
      id: urgencyOption.id,
      title: urgencyOption.name || "Unknown Urgency",
      color: urgencyOption.color || "#d1d5db",
      items: [],
    }));

  urgencyGroups.push({
    id: "none",
    title: "None",
    color: "#e5e7eb",
    items: [],
    isFallback: true,
  });
  urgencyGroups.push({
    id: "uncategorised",
    title: "Uncategorised",
    color: "#d1d5db",
    items: [],
    isFallback: true,
  });

  return urgencyGroups;
}

function appendClosedGroup(groups, showClosed) {
  if (showClosed) {
    groups.push({
      id: "closed",
      title: "Closed",
      color: "#d1d5db",
      isClosed: true,
      items: [],
    });
  }

  return groups;
}

function buildAssigneeGroups(showClosed) {
  const groups = [
    { id: "personal", title: "Personal", items: [] },
    { id: "owned-by-me", title: "Owned by Me", items: [] },
    { id: "assigned-to-me", title: "Assigned to Me", items: [] },
  ];

  return appendClosedGroup(groups, showClosed);
}

function buildProviderGroups(items, showClosed) {
  const providerIds = new Set();

  (items || []).forEach((item) => {
    const providers = Array.isArray(item?.provider) ? item.provider : [];
    providers.forEach((providerId) => {
      if (providerId) {providerIds.add(providerId);}
    });
  });

  const providerGroups = Array.from(providerIds)
    .map((providerId) => ({
      id: providerId,
      title: getPatientProviderDisplayName(providerId) || providerId,
    }))
    .sort((a, b) => a.title.localeCompare(b.title))
    .map((provider) => ({
      ...provider,
      items: [],
    }));

  providerGroups.push({
    id: "none",
    title: "None",
    items: [],
    isFallback: true,
  });
  providerGroups.push({
    id: "uncategorised",
    title: "Uncategorised",
    items: [],
    isFallback: true,
  });

  return appendClosedGroup(providerGroups, showClosed);
}

function buildTechnicianGroups(showClosed) {
  const technicianGroups = (appData?.config?.maintenance?.technicians || [])
    .filter((technician) => technician?.isActive !== false)
    .slice()
    .sort((a, b) => {
      const orderDiff = (a?.order ?? 999) - (b?.order ?? 999);
      if (orderDiff !== 0) {return orderDiff;}
      return (a?.name || "").localeCompare(b?.name || "");
    })
    .map((technician) => ({
      id: technician.name,
      title: technician.name || "Unknown Technician",
      items: [],
    }));

  technicianGroups.push({
    id: "other",
    title: "Other",
    items: [],
    isFallback: true,
  });
  technicianGroups.push({
    id: "none",
    title: "Unassigned",
    items: [],
    isFallback: true,
  });

  return appendClosedGroup(technicianGroups, showClosed);
}

function buildLabGroups(showClosed) {
  const internalLabel = appData?.config?.labs?.internalLabLabel ||
    "Internal lab";
  const labGroups = [
    {
      id: "internal",
      title: internalLabel,
      items: [],
    },
    ...(appData?.config?.labs?.externalLabs || [])
      .filter((lab) => lab?.isActive !== false)
      .slice()
      .sort((a, b) => {
        const orderDiff = (a?.order ?? 999) - (b?.order ?? 999);
        if (orderDiff !== 0) {return orderDiff;}
        return (a?.name || "").localeCompare(b?.name || "");
      })
      .map((lab) => ({
        id: lab.id,
        title: lab.name || "Unknown Lab",
        items: [],
      })),
  ];

  labGroups.push({
    id: "other",
    title: "Other",
    items: [],
    isFallback: true,
  });

  return appendClosedGroup(labGroups, showClosed);
}

function buildLabsAssigneeGroups(showClosed) {
  const configuredGroups = getUsersWithPermission("labs")
    .slice()
    .sort((a, b) => {
      return (getUserDisplayNameById(a.id) || "")
        .localeCompare(getUserDisplayNameById(b.id) || "");
    })
    .map((user) => ({
      id: user.id,
      title: getUserDisplayNameById(user.id) || "Unknown User",
      items: [],
    }));

  configuredGroups.push({
    id: "none",
    title: "Unassigned",
    items: [],
    isFallback: true,
  });
  configuredGroups.push({
    id: "other",
    title: "Other",
    items: [],
    isFallback: true,
  });

  return appendClosedGroup(configuredGroups, showClosed);
}

function appendItemToGroup(groupsById, groupId, item) {
  if (!groupsById.has(groupId)) {return;}
  groupsById.get(groupId).items.push(item);
}

function groupByStatus(groupsById, itemType, items, showClosed) {
  const statuses = getBoardStatusConfig(itemType);
  const statusField = getBoardStatusField(itemType);
  const hasStatusCategories = getBoardSortedCategories(itemType).length > 0;

  items.forEach((item) => {
    if (!showClosed && isItemClosed(item)) {
      return;
    }

    const statusOption = statuses.find((status) => status.id === item?.[statusField]);
    const categoryId = hasStatusCategories ?
      statusOption?.category || "uncategorised" :
      statusOption?.id || "uncategorised";

    appendItemToGroup(groupsById, categoryId, item);
  });
}

function groupByDate(groupsById, itemType, items, dateGroupingMode, showClosed, taskList) {
  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const groupId = getDateGroupId(
      getResolvedCardDate(itemType, item, taskList),
      dateGroupingMode,
    );
    appendItemToGroup(groupsById, groupId, item);
  });
}

function groupByType(groupsById, itemType, items, showClosed) {
  const knownTypeIds = new Set(
    getBoardTypeConfig(itemType).map((typeOption) => typeOption.id),
  );

  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const rawTypes = Array.isArray(item?.types) ? item.types.filter(Boolean) : [];

    if (rawTypes.length === 0) {
      appendItemToGroup(groupsById, "none", item);
      return;
    }

    const uniqueTypeIds = [...new Set(rawTypes)];
    let pushedFallback = false;

    uniqueTypeIds.forEach((typeId) => {
      if (knownTypeIds.has(typeId)) {
        appendItemToGroup(groupsById, typeId, item);
      } else if (!pushedFallback) {
        appendItemToGroup(groupsById, "uncategorised", item);
        pushedFallback = true;
      }
    });
  });
}

function groupByUrgency(groupsById, itemType, items, showClosed) {
  const knownUrgencyIds = new Set(
    getBoardUrgencyConfig(itemType).map((urgencyOption) => urgencyOption.id),
  );

  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const urgencyId = item?.priority || null;

    if (!urgencyId) {
      appendItemToGroup(groupsById, "none", item);
      return;
    }

    appendItemToGroup(
      groupsById,
      knownUrgencyIds.has(urgencyId) ? urgencyId : "uncategorised",
      item,
    );
  });
}

function groupByTaskAssignee(groupsById, items, showClosed) {
  const currentUserId = state?.currentUser?.uid;

  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const assignedUsers = Array.isArray(item?.assigned_users) ?
      item.assigned_users.filter(Boolean) : [];
    const ownerId = item?.owner || null;
    const assignedToOthers = assignedUsers.some(
      (userId) => userId !== currentUserId,
    );

    if (ownerId === currentUserId && !assignedToOthers) {
      appendItemToGroup(groupsById, "personal", item);
      return;
    }

    if (ownerId === currentUserId && assignedToOthers) {
      appendItemToGroup(groupsById, "owned-by-me", item);
      return;
    }

    if (assignedUsers.includes(currentUserId)) {
      appendItemToGroup(groupsById, "assigned-to-me", item);
    }
  });
}

function groupByLabsAssignee(groupsById, items, showClosed) {
  const knownAssigneeIds = new Set(
    Array.from(groupsById.keys()).filter(
      (groupId) =>
        groupId !== "none" &&
        groupId !== "other" &&
        groupId !== "closed",
    ),
  );

  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const assigneeIds = Array.isArray(item?.assigned_users) ?
      item.assigned_users.filter(Boolean) :
      [];

    if (assigneeIds.length === 0) {
      appendItemToGroup(groupsById, "none", item);
      return;
    }

    let pushedFallback = false;
    [...new Set(assigneeIds)].forEach((assigneeId) => {
      if (knownAssigneeIds.has(assigneeId)) {
        appendItemToGroup(groupsById, assigneeId, item);
      } else if (!pushedFallback) {
        appendItemToGroup(groupsById, "other", item);
        pushedFallback = true;
      }
    });
  });
}

function groupByProvider(groupsById, items, showClosed) {
  const knownProviderIds = new Set(
    Array.from(groupsById.keys()).filter(
      (groupId) =>
        groupId !== "none" &&
        groupId !== "uncategorised" &&
        groupId !== "closed",
    ),
  );

  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const rawProviders = Array.isArray(item?.provider) ?
      item.provider.filter(Boolean) : [];

    if (rawProviders.length === 0) {
      appendItemToGroup(groupsById, "none", item);
      return;
    }

    const uniqueProviderIds = [...new Set(rawProviders)];
    let pushedFallback = false;

    uniqueProviderIds.forEach((providerId) => {
      if (knownProviderIds.has(providerId)) {
        appendItemToGroup(groupsById, providerId, item);
      } else if (!pushedFallback) {
        appendItemToGroup(groupsById, "uncategorised", item);
        pushedFallback = true;
      }
    });
  });
}

function groupByTechnician(groupsById, items, showClosed) {
  const configuredTechnicians = new Set(
    (appData?.config?.maintenance?.technicians || [])
      .filter((technician) => technician?.isActive !== false)
      .map((technician) => String(technician?.name || "").trim())
      .filter(Boolean),
  );

  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const technician = String(item?.technician || "").trim();
    if (!technician) {
      appendItemToGroup(groupsById, "none", item);
      return;
    }

    appendItemToGroup(
      groupsById,
      configuredTechnicians.has(technician) ? technician : "other",
      item,
    );
  });
}

function groupByLab(groupsById, items, showClosed) {
  const knownLabIds = new Set(
    Array.from(groupsById.keys()).filter(
      (groupId) => groupId !== "other" && groupId !== "closed",
    ),
  );

  items.forEach((item) => {
    if (isItemClosed(item)) {
      if (showClosed) {
        appendItemToGroup(groupsById, "closed", item);
      }
      return;
    }

    const labId = item?.lab || "";
    appendItemToGroup(
      groupsById,
      knownLabIds.has(labId) ? labId : "other",
      item,
    );
  });
}

/**
 * Groups board items for the configured board/table grouping mode.
 * @param {object} params Grouping parameters.
 * @param {string} params.itemType Parent-board item type.
 * @param {Array<object>} [params.items=[]] Items to group.
 * @param {string} [params.groupBy="status"] Grouping mode.
 * @param {string} [params.dateGroupingMode="daily"] Date grouping mode.
 * @param {boolean} [params.showClosed=false] Whether closed items are shown.
 * @param {Array<object>} [params.taskList=appData?.tasks || []] Related tasks.
 * @returns {Array<object>} Group descriptors with items.
 */
export function buildGroupedItems({
  itemType,
  items = [],
  groupBy = "status",
  dateGroupingMode = "daily",
  showClosed = false,
  taskList = appData?.tasks || [],
}) {
  let groups;
  let resolvedGroupBy = groupBy;

  if (resolvedGroupBy === "provider" &&
    itemType !== "patient" &&
    itemType !== "lab") {
    resolvedGroupBy = "status";
  }
  if (resolvedGroupBy === "assignee" &&
    itemType !== "task" &&
    itemType !== "lab") {
    resolvedGroupBy = "status";
  }
  if (resolvedGroupBy === "urgency" && itemType === "asset") {
    resolvedGroupBy = "status";
  }
  if (resolvedGroupBy === "lab" && itemType !== "lab") {
    resolvedGroupBy = "status";
  }
  if (resolvedGroupBy === "technician" && itemType !== "maintenance") {
    resolvedGroupBy = "status";
  }

  switch (resolvedGroupBy) {
    case "date":
      groups = buildDateGroups(dateGroupingMode, showClosed);
      break;
    case "type":
      groups = appendClosedGroup(buildTypeGroups(itemType), showClosed);
      break;
    case "urgency":
      groups = appendClosedGroup(buildUrgencyGroups(itemType), showClosed);
      break;
    case "assignee":
      groups = itemType === "lab" ?
        buildLabsAssigneeGroups(showClosed) :
        buildAssigneeGroups(showClosed);
      break;
    case "provider":
      groups = buildProviderGroups(items, showClosed);
      break;
    case "lab":
      groups = buildLabGroups(showClosed);
      break;
    case "technician":
      groups = buildTechnicianGroups(showClosed);
      break;
    case "status":
    default:
      groups = buildStatusGroups(itemType, showClosed);
      break;
  }

  const groupsById = new Map(groups.map((group) => [group.id, group]));

  switch (resolvedGroupBy) {
    case "date":
      groupByDate(groupsById, itemType, items, dateGroupingMode, showClosed, taskList);
      break;
    case "type":
      groupByType(groupsById, itemType, items, showClosed);
      break;
    case "urgency":
      groupByUrgency(groupsById, itemType, items, showClosed);
      break;
    case "assignee":
      if (itemType === "lab") {
        groupByLabsAssignee(groupsById, items, showClosed);
      } else {
        groupByTaskAssignee(groupsById, items, showClosed);
      }
      break;
    case "provider":
      groupByProvider(groupsById, items, showClosed);
      break;
    case "technician":
      groupByTechnician(groupsById, items, showClosed);
      break;
    case "lab":
      groupByLab(groupsById, items, showClosed);
      break;
    case "status":
    default:
      groupByStatus(groupsById, itemType, items, showClosed);
      break;
  }

  groups.forEach((group) => {
    if (group?.id === "closed" || group?.isClosed === true) {
      group.items.sort((a, b) => getUpdatedAtValue(b) - getUpdatedAtValue(a));
    }
  });

  return groups;
}
